
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
 
  Grid,
  Divider,
  Chip,
  Avatar,
  Stack,
  Paper,
  
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccountBalance as BankIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Receipt as ReceiptIcon,
  ContactPhone as ContactIcon,
  VerifiedUser as VerifiedIcon,
} from '@mui/icons-material';
import Layout from '../components/layout/Layout';

const CustomerInvoiceProfile = () => {
  const navigate = useNavigate();
  const {
    selectedCompanyInfo,
    selectedAddress,
    selectedContactInfo,
    selectedBusinessInfo,
    selectedTaxInfo,
    selectedComplianceInfo,
    selectedBankingInfo
  } = useSelector((state) => state.kyc);

  const renderValue = (value) => value || 'N/A';
  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'N/A';
  const formatArray = (arr) => arr?.length ? arr.join(', ') : 'N/A';

  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const cardHoverVariants = {
    hover: {
      y: -4,
      transition: {
        type: "spring",
        stiffness: 300
      }
    }
  };

  const renderSection = ({ title, icon: Icon, content, color = 'primary' }) => (
    <motion.div variants={itemVariants}>
      <Paper
        component={motion.div}
        variants={cardHoverVariants}
        whileHover="hover"
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid',
          borderColor: 'grey.100',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${color === 'primary' ? '#EB5A3C' : '#3498DB'}, ${color === 'primary' ? '#FF7A5C' : '#5DADE2'})`,
          }
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Avatar
            sx={{
              bgcolor: `${color}.main`,
              color: 'white',
              width: 48,
              height: 48,
            }}
          >
            <Icon />
          </Avatar>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              background: `linear-gradient(45deg, ${color === 'primary' ? '#EB5A3C' : '#3498DB'}, ${color === 'primary' ? '#FF7A5C' : '#5DADE2'})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {title}
          </Typography>
        </Stack>
        <Divider sx={{ mb: 3, bgcolor: 'grey.200' }} />
        <Grid container spacing={3}>
          {content}
        </Grid>
      </Paper>
    </motion.div>
  );

  const renderInfoItem = ({ label, value, icon: Icon, chipColor }) => (
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.100',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: 'primary.50',
            borderColor: 'primary.200',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(235, 90, 60, 0.1)',
          }
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: 'primary.main' }} />}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
            {label}
          </Typography>
        </Stack>
        {chipColor ? (
          <Chip
            label={value}
            color={chipColor}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 500 }}
          />
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
            {value}
          </Typography>
        )}
      </Box>
    </Grid>
  );

  const renderContactBlock = ({ title, contact, icon: Icon }) => (
    <Grid item xs={12} md={6}>
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.100',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: 'primary.50',
            borderColor: 'primary.200',
            transform: 'translateY(-2px)',
          }
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            <Icon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {title}
          </Typography>
        </Stack>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60 }}>Name:</Typography>
            <Typography variant="body2">{renderValue(contact?.name)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60 }}>Position:</Typography>
            <Typography variant="body2">{renderValue(contact?.position)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="body2">{renderValue(contact?.phone)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 16, color: 'info.main' }} />
            <Typography variant="body2">{renderValue(contact?.email)}</Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );

  const renderAddressBlock = ({ title, address, icon: Icon }) => (
    <Grid item xs={12} md={6}>
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.100',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: 'secondary.50',
            borderColor: 'secondary.200',
            transform: 'translateY(-2px)',
          }
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', color: 'text.primary', width: 40, height: 40 }}>
            <Icon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.dark' }}>
            {title}
          </Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {renderValue(address?.street)}
          </Typography>
          <Typography variant="body2">
            {renderValue(address?.city)}, {renderValue(address?.state)}
          </Typography>
          <Typography variant="body2">
            {renderValue(address?.postalCode)}, {renderValue(address?.country)}
          </Typography>
        </Stack>
      </Paper>
    </Grid>
  );

  if (!selectedCompanyInfo?.legalName) {
    return (
      <Layout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Paper
              elevation={3}
              sx={{
                p: 6,
                borderRadius: 3,
                maxWidth: 500,
                mx: 'auto',
                background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
              }}
            >
              <Typography variant="h4" gutterBottom color="text.primary" sx={{ fontWeight: 600 }}>
                No KYC Data Available
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Please ensure that KYC information has been properly loaded.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Go Back
              </Button>
            </Paper>
          </Box>
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #EB5A3C 0%, #FF7A5C 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <VerifiedIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {renderValue(selectedCompanyInfo.legalName)}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Chip
                      label="KYC Verified"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                      }}
                    />
                    <Chip
                      label={selectedCompanyInfo.companyType || 'Company'}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </motion.div>

         
          {renderSection({
            title: 'Company Information',
            icon: BusinessIcon,
            content: (
              <>
                {renderInfoItem({ label: 'Trade Name', value: renderValue(selectedCompanyInfo.tradeName), icon: BusinessIcon })}
                {renderInfoItem({ label: 'Company Type', value: renderValue(selectedCompanyInfo.companyType), chipColor: 'primary' })}
                {renderInfoItem({ label: 'Registration Number', value: renderValue(selectedCompanyInfo.registrationNumber) })}
                {renderInfoItem({ label: 'Date of Incorporation', value: formatDate(selectedCompanyInfo.dateOfIncorporation) })}
                {renderInfoItem({ label: 'Country of Incorporation', value: renderValue(selectedCompanyInfo.countryOfIncorporation), icon: LocationIcon })}
              </>
            )
          })}

          {/* Address Information */}
          {renderSection({
            title: 'Address Information',
            icon: LocationIcon,
            color: 'info',
            content: (
              <>
                {renderAddressBlock({ 
                  title: 'Registered Office', 
                  address: selectedAddress?.registeredOffice,
                  icon: BusinessIcon
                })}
                {renderAddressBlock({ 
                  title: 'Operating Office', 
                  address: selectedAddress?.operatingOffice,
                  icon: LocationIcon
                })}
              </>
            )
          })}

          {/* Contact Information */}
          {renderSection({
            title: 'Contact Information',
            icon: ContactIcon,
            content: (
              <>
                {renderContactBlock({ 
                  title: 'Primary Contact', 
                  contact: selectedContactInfo?.primaryContact,
                  icon: ContactIcon
                })}
                {renderContactBlock({ 
                  title: 'Secondary Contact', 
                  contact: selectedContactInfo?.secondaryContact,
                  icon: PhoneIcon
                })}
              </>
            )
          })}

          {/* Business Information */}
          {renderSection({
            title: 'Business Information',
            icon: AssessmentIcon,
            content: (
              <>
                {renderInfoItem({ label: 'Nature of Business', value: renderValue(selectedBusinessInfo?.natureOfBusiness) })}
                {renderInfoItem({ label: 'Industry', value: renderValue(selectedBusinessInfo?.industry), chipColor: 'secondary' })}
                {renderInfoItem({ label: 'Annual Turnover', value: renderValue(selectedBusinessInfo?.annualTurnover) })}
                {renderInfoItem({ label: 'Number of Employees', value: renderValue(selectedBusinessInfo?.numberOfEmployees) })}
                {renderInfoItem({ label: 'Main Activities', value: formatArray(selectedBusinessInfo?.mainActivities) })}
                {renderInfoItem({ label: 'Products/Services', value: formatArray(selectedBusinessInfo?.productsOrServices) })}
                {renderInfoItem({ label: 'Major Markets', value: formatArray(selectedBusinessInfo?.majorMarkets) })}
              </>
            )
          })}

          {/* Tax Information */}
          {renderSection({
            title: 'Tax Information',
            icon: ReceiptIcon,
            color: 'info',
            content: (
              <>
                {renderInfoItem({ label: 'Tax ID Number', value: renderValue(selectedTaxInfo?.taxIdentificationNumber), icon: ReceiptIcon })}
                {renderInfoItem({ label: 'VAT/GST Number', value: renderValue(selectedTaxInfo?.vatGstNumber), icon: ReceiptIcon })}
                {renderInfoItem({ label: 'Country of Tax Residence', value: renderValue(selectedTaxInfo?.countryOfTaxResidence), icon: LocationIcon })}
              </>
            )
          })}

          {/* Compliance Information */}
          {renderSection({
            title: 'Compliance Information',
            icon: SecurityIcon,
            content: (
              <>
                {renderInfoItem({ label: 'AML Compliance Officer', value: renderValue(selectedComplianceInfo?.amlComplianceOfficer?.name), icon: SecurityIcon })}
                {renderInfoItem({ label: 'AML Officer Contact', value: renderValue(selectedComplianceInfo?.amlComplianceOfficer?.contactInfo), icon: ContactIcon })}
              </>
            )
          })}

          {/* Banking Information */}
          {selectedBankingInfo?.length > 0 && renderSection({
            title: 'Banking Information',
            icon: BankIcon,
            color: 'info',
            content: selectedBankingInfo.map((bank, index) => (
              <Grid item xs={12} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'info.200',
                    bgcolor: 'info.50',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(52, 152, 219, 0.15)',
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                      <BankIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
                      Bank Account {index + 1}
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Bank Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{renderValue(bank.bankName)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Account Number</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>{renderValue(bank.accountNumber)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Bank Address</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{renderValue(bank.bankAddress)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>SWIFT Code</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>{renderValue(bank.swiftCode)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
                {index < selectedBankingInfo.length - 1 && <Box sx={{ mt: 2 }} />}
              </Grid>
            ))
          })}

          {/* Back Button */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 3,
                  background: 'linear-gradient(45deg, #EB5A3C, #FF7A5C)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #C54428, #EB5A3C)',
                  }
                }}
              >
                Go Back
              </Button>
            </Box>
          </motion.div>
        </Box>
      </motion.div>
    </Layout>
  );
};

export default CustomerInvoiceProfile;
